import {
  ActionReducerMap,
  createFeatureSelector,
  createSelector,
  MemoizedSelector,
  select
} from '@ngrx/store';
import { Observable, pipe, UnaryFunction } from 'rxjs';
import { BasicDeal, Deal, DealVersion } from '../types/deal.types';
import { benchmarkCurvesReducer } from './benchmark-curves/reducer';
import {
  bidsReducer,
  getBids,
  selectBidsCompareColumn,
  selectBidsCompareList,
  selectBidsCompareMode,
  selectBidsError,
  selectBidsLoaded,
  selectBidsLoading
} from './bids/reducer';
import { BidsState } from './bids/state';
import { calendarFiltersReducer } from './calendar-filter/reducer';
import { CalendarFiltersState } from './calendar-filter/state';
import {
  calendarReducer,
  getCalendarAssignedDeals,
  getCalendarDealAssignments,
  getCalendarDeals,
  getCalendarMaturityAnalyticsColumn,
  getCalendarWeekOf,
  selectCalendarError,
  selectCalendarLoading
} from './calendar/reducer';
import { CalendarState } from './calendar/state';
import {
  dealReducer,
  selectCurrentDealVersion,
  selectDeal
} from './deal/reducer';
import { DealState } from './deal/state';
import {
  followsReducer,
  selectDealsFollowInProcess,
  selectDealsGroupFollowInProcess,
  selectFollows,
  selectGroupFollows
} from './follows/reducer';
import {
  getManagerDeals,
  managerReducer,
  selectManagerError,
  selectManagerLoaded,
  selectManagerLoading,
  selectManagerSelectionModel,
  selectManagerSortModel,
  selectManagerStatus
} from './manager/reducer';
import { ManagerState } from './manager/state';
import {
  getMonitorDeals,
  getMonitorMetrics,
  monitorReducer,
  selectMonitorCardsModels,
  selectMonitorCardSortModel,
  selectMonitorCardStatus,
  selectMonitorError,
  selectMonitorLoaded,
  selectMonitorLoading,
  selectMonitorStatus
} from './monitor/reducer';
import { MonitorMetrics, MonitorState } from './monitor/state';
import { wiresReducer } from './wires/reducer';

import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { ManagerGridColumnName } from '../grid/types/column.types';
import { SortOrder } from '../grid/types/sorting.types';
import { MonitorCardTypes } from '../types/monitor.types';
import { equalsCompare } from '../utils/comparers/primitive';
import { analyticsReducer } from './analytics/reducer';
import { AnalyticsStoreState } from './analytics/state';
import * as fromAssignmentGroups from './assignment-groups/reducer';
import { AssignmentGroupsState } from './assignment-groups/state';
import * as fromAssignments from './assignments/reducer';
import { AssignmentsState } from './assignments/state';
import { BenchmarkCurveStoreState } from './benchmark-curves/state';
import { bidResultsReducer } from './bid-results/reducer';
import { BidResultsState } from './bid-results/state';
import {
  calendarSortingReducer,
  getCalendarSortModel
} from './calendar-sorting/reducer';
import { CalendarSortingState } from './calendar-sorting/state';
import * as fromCasaSettings from './casa-settings/reducer';
import { CasaSettingsState } from './casa-settings/state';
import { dealMetricsReducer } from './deal-metrics/reducer';
import { DealMetricsState } from './deal-metrics/state';
import { FollowsState } from './follows/state';
import {
  managerFiltersReducer,
  selectManagerCardsFilters,
  selectManagerDefaults,
  selectManagerDefaultsUnsaved,
  selectManagerFilters
} from './manager-filters/reducer';
import { ManagerFiltersState } from './manager-filters/state';
import { monitorFiltersReducer } from './monitor-filters/reducer';
import { MonitorFiltersState } from './monitor-filters/state';
import * as fromOrders from './orders/reducer';
import { OrdersState } from './orders/state';
import { seriesDetailsReducer } from './series-details/reducer';
import { SeriesDetailsState } from './series-details/state';
import * as fromSession from './session/reducer';
import { SessionState } from './session/state';
import { getUSTreasuries, usTreasuriesReducer } from './treasuries/reducer';
import { USTreasuriesState } from './treasuries/state';
import { userPermissionsReducer } from './user-permissions/reducer';
import { UserPermissionsState } from './user-permissions/state';
import {
  getSavedCalendarFilters,
  userPreferencesReducer
} from './user-preferences/reducer';
import { UserPreferencesState } from './user-preferences/state';
import { usersReducer } from './users/reducer';
import { UsersState } from './users/state';
import { WiresState } from './wires/state';

import {
  DesignationIssue,
  DesignationsCardTypes
} from '../types/designation.types';
import { dealCardsBidResultsReducer } from './deal-cards-bid-results/reducer';
import { DealCardsBidResultsState } from './deal-cards-bid-results/state';
import { designationsTabReducer } from './designations-tab/reducer';
import { DesignationsTabState } from './designations-tab/state';
import {
  designationsReducer,
  getAllotments,
  getAllotmentsBreakdownsLoadingState,
  getAllotmentsData,
  getAllotmentsHasDifferentBreakdowns,
  getAllotmentsLoadingState,
  getBreakdownFormDirtyState,
  getBreakdowns,
  getBreakdownsSubmissionError,
  getDesignationDealInfo,
  getDesignationDealInfoLoadedState,
  getDesignationDealInfoLoadingState,
  getDesignationDealPriorityPolicy,
  getDesignationIssues,
  getDesignationIssuesLoadingState,
  getDesignationsFinalStatus,
  getIssuesByDesignationStatus,
  getSelectedAllotments,
  hasBreakdownsSubmissionError,
  isDesiApisHasError,
  isDesiApisLoaded,
  isDesiBreakdownsSubmitting,
  selectDesignationsCardSortModel
} from './designations/reducer';
import { DesignationEntryState, DesignationState } from './designations/state';

export interface State {
  deal: DealState;
  bids: BidsState;
  bidResults: BidResultsState;
  dealCardsBidResults: DealCardsBidResultsState;
  calendar: CalendarState;
  calendarFilters: CalendarFiltersState;
  calendarSorting: CalendarSortingState;
  seriesDetails: SeriesDetailsState;
  analytics: AnalyticsStoreState;
  benchmarkCurves: BenchmarkCurveStoreState;
  monitor: MonitorState;
  monitorFilters: MonitorFiltersState;
  manager: ManagerState;
  managerFilters: ManagerFiltersState;
  follows: FollowsState;
  wires: WiresState;
  userPreferences: UserPreferencesState;
  userPermissions: UserPermissionsState;
  users: UsersState;
  session: SessionState;
  casaSettings: CasaSettingsState;
  orders: OrdersState;
  assignments: AssignmentsState;
  assignmentGroups: AssignmentGroupsState;
  dealMetrics: DealMetricsState;
  treasuries: USTreasuriesState;
  designations: DesignationState;
  designationsTab: DesignationsTabState;
}

export interface SharedState {
  'muni-shared': State;
}

export const reducer: ActionReducerMap<State> = {
  deal: dealReducer,
  bids: bidsReducer,
  bidResults: bidResultsReducer,
  dealCardsBidResults: dealCardsBidResultsReducer,
  calendar: calendarReducer,
  calendarFilters: calendarFiltersReducer,
  calendarSorting: calendarSortingReducer,
  analytics: analyticsReducer,
  benchmarkCurves: benchmarkCurvesReducer,
  monitor: monitorReducer,
  monitorFilters: monitorFiltersReducer,
  manager: managerReducer,
  managerFilters: managerFiltersReducer,
  follows: followsReducer,
  wires: wiresReducer,
  userPreferences: userPreferencesReducer,
  userPermissions: userPermissionsReducer,
  users: usersReducer,
  seriesDetails: seriesDetailsReducer,
  session: fromSession.sessionReducer,
  casaSettings: fromCasaSettings.casaSettingsReducer,
  orders: fromOrders.ordersReducer,
  assignments: fromAssignments.assignmentsReducer,
  assignmentGroups: fromAssignmentGroups.assignmentGroupsReducer,
  dealMetrics: dealMetricsReducer,
  treasuries: usTreasuriesReducer,
  designations: designationsReducer,
  designationsTab: designationsTabReducer
};

export const selectSharedState = createFeatureSelector<State>('muni-shared');

export const getDealStoreState = createSelector(
  selectSharedState,
  (state: State) => state.deal
);

export const getFollowsStoreState = createSelector(
  selectSharedState,
  (state: State) => state.follows
);

export const getDeal: MemoizedSelector<unknown, Deal> = createSelector(
  getDealStoreState,
  selectDeal
);

export const getDealFollows = createSelector(
  getFollowsStoreState,
  selectFollows
);

export const getDealGroupFollows = createSelector(
  getFollowsStoreState,
  selectGroupFollows
);

export const getDealsFollowInProcess = createSelector(
  getFollowsStoreState,
  selectDealsFollowInProcess
);

export const getDealsGroupFollowInProcess = createSelector(
  getFollowsStoreState,
  selectDealsGroupFollowInProcess
);

export const selectSessionState = createSelector(
  selectSharedState,
  (state: State) => state.session
);

export const getSession = createSelector(
  selectSessionState,
  fromSession.getSession
);

export const getSessionLoadedState = createSelector(
  selectSessionState,
  fromSession.getSessionLoaded
);

export const getCurrentDealVersionDistinct: UnaryFunction<
  Observable<unknown>,
  Observable<DealVersion>
> = pipe(
  select(getDealStoreState),
  distinctUntilChanged(equalsCompare),
  selectCurrentDealVersion,
  distinctUntilChanged(equalsCompare)
);

export const selectBidsState = createSelector(
  selectSharedState,
  (state: State) => state.bids
);

export const selectBids: MemoizedSelector<unknown, Deal[]> = createSelector(
  selectBidsState,
  getBids
);

export const selectBidsLoadingState: MemoizedSelector<
  unknown,
  boolean
> = createSelector(selectBidsState, selectBidsLoading);

export const selectBidsLoadedState: MemoizedSelector<
  unknown,
  boolean
> = createSelector(selectBidsState, selectBidsLoaded);

export const selectBidsErrorState: MemoizedSelector<
  unknown,
  string
> = createSelector(selectBidsState, selectBidsError);

export const selectBidsCompareModeState: MemoizedSelector<
  unknown,
  boolean
> = createSelector(selectBidsState, selectBidsCompareMode);
export const selectBidsCompareListState: MemoizedSelector<
  unknown,
  number[]
> = createSelector(selectBidsState, selectBidsCompareList);
export const selectBidsCompareColumnState: MemoizedSelector<
  unknown,
  string
> = createSelector(selectBidsState, selectBidsCompareColumn);

export const selectCalendarState = createSelector(
  selectSharedState,
  (state: State) => state.calendar
);

export const selectCalendarDeals: MemoizedSelector<
  unknown,
  BasicDeal[]
> = createSelector(selectCalendarState, getCalendarDeals);

export const selectCalendarDealAssignments = (dealId: string) =>
  createSelector(selectCalendarState, getCalendarDealAssignments(dealId));

export const selectAssignedDeals = (userId: string) =>
  createSelector(selectCalendarState, getCalendarAssignedDeals(userId));

export const selectCalendarLoadingState: MemoizedSelector<
  unknown,
  boolean
> = createSelector(selectCalendarState, selectCalendarLoading);

export const selectCalendarErrorState: MemoizedSelector<
  unknown,
  string
> = createSelector(selectCalendarState, selectCalendarError);

export const selectCalendarSavedFilterId: MemoizedSelector<
  unknown,
  string
> = createSelector(
  selectCalendarState,
  (state: CalendarState) => state.selectedSavedFilterId
);

export const selectCalendarWeekOf = createSelector(
  selectCalendarState,
  getCalendarWeekOf
);

export const selectCalendarMaturityAnalyticsColumn = createSelector(
  selectCalendarState,
  getCalendarMaturityAnalyticsColumn
);

export const selectDistinctCalendarWeekOf = pipe(
  select(selectCalendarWeekOf),
  distinctUntilChanged()
);

export const selectAnalyticsState = createSelector(
  selectSharedState,
  (state: State) => state.analytics
);

export const selectBenchmarkCurveState = createSelector(
  selectSharedState,
  (state: State) => state.benchmarkCurves
);

export const selectMonitorState = createSelector(
  selectSharedState,
  (state: State) => state.monitor
);

export const selectMonitorMetricsState = createSelector(
  selectSharedState,
  (state: State) => state.monitor
);

export const selectMonitorMetrics: MemoizedSelector<
  unknown,
  MonitorMetrics<Partial<DealMetricsState>>
> = createSelector(selectMonitorState, getMonitorMetrics);

export const selectMonitorDeals: MemoizedSelector<
  unknown,
  DealVersion[]
> = createSelector(selectMonitorState, getMonitorDeals);

export const selectMonitorLoadingState: MemoizedSelector<
  unknown,
  boolean
> = createSelector(selectMonitorState, selectMonitorLoading);

export const selectMonitorLoadedState: MemoizedSelector<
  unknown,
  boolean
> = createSelector(selectMonitorState, selectMonitorLoaded);

export const selectMonitorErrorState: MemoizedSelector<
  unknown,
  string
> = createSelector(selectMonitorState, selectMonitorError);
export const selectMonitorStatusState: MemoizedSelector<
  unknown,
  boolean
> = createSelector(selectMonitorState, selectMonitorStatus);

export const selectUsers = createSelector(selectSharedState, (s) => s.users);

export const selectMonitorCardsModelsState = createSelector(
  selectMonitorState,
  selectMonitorCardsModels
);

export const selectMonitorCardStatusState = (
  key: MonitorCardTypes
): MemoizedSelector<unknown, boolean> =>
  createSelector(selectMonitorState, selectMonitorCardStatus(key));

export const getWiresStoreState = createSelector(
  selectSharedState,
  (state: State) => state.wires
);

export const selectCalendarFiltersState = createSelector(
  selectSharedState,
  (state: State) => state.calendarFilters
);

export const selectUserPreferencesState = createSelector(
  selectSharedState,
  (state: State) => state.userPreferences
);

export const selectSavedCalendarFilters = createSelector(
  selectUserPreferencesState,
  getSavedCalendarFilters
);

export const selectUserPermissionsState = createSelector(
  selectSharedState,
  (state: State) => state.userPermissions
);

export const selectIpreoAccountUserId = pipe(
  select(selectUserPermissionsState),
  filter((s: UserPermissionsState) => !!s.userPermissions),
  map((s: UserPermissionsState) => s.userPermissions.ipreoAccountUserId)
);

export const getSeriesDetailsState = createSelector(
  selectSharedState,
  (state: State) => state.seriesDetails
);

export const getSeriesDetailsMargTaxRate = createSelector(
  getSeriesDetailsState,
  (state: SeriesDetailsState) => state.margTaxRate
);

export const getSeriesDetailsMargTaxRateValue = createSelector(
  getSeriesDetailsState,
  (state: SeriesDetailsState) => state.defaultMarginalTaxRateValue
);

export const getUsers = createSelector(
  selectSharedState,
  (state: State) => state.users
);

export const selectCalendarSortingState = createSelector(
  selectSharedState,
  (state: State) => state.calendarSorting
);

export const selectCalendarSortModel = createSelector(
  selectCalendarSortingState,
  getCalendarSortModel
);

export const getMonitorCardSortModelSelector = (key: MonitorCardTypes) =>
  createSelector(selectMonitorState, selectMonitorCardSortModel(key));

export const selectMonitorFiltersState = createSelector(
  selectSharedState,
  (state: State) => state.monitorFilters
);

export const selectCasaSettingsState = createSelector(
  selectSharedState,
  (state: State) => state.casaSettings
);

export const getCasaSettings = createSelector(
  selectCasaSettingsState,
  fromCasaSettings.getSettings
);

export const getOrdersStoreState = createSelector(
  selectSharedState,
  (state: State) => state.orders
);

export const selectOrders = createSelector(
  getOrdersStoreState,
  fromOrders.getOrders
);

export const selectAssignmentGroups: MemoizedSelector<
  unknown,
  AssignmentGroupsState
> = createSelector(selectSharedState, (state: State) => state.assignmentGroups);

export const selectAssignments: MemoizedSelector<
  unknown,
  AssignmentsState
> = createSelector(selectSharedState, (state: State) => state.assignments);

export const selectDealMetrics: MemoizedSelector<
  unknown,
  DealMetricsState
> = createSelector(selectSharedState, (state: State) => state.dealMetrics);

export const selectBidResults: MemoizedSelector<
  unknown,
  BidResultsState
> = createSelector(selectSharedState, (state: State) => state.bidResults);

export const selectBidResultsLoading: MemoizedSelector<
  BidResultsState,
  boolean
> = createSelector(selectBidResults, (state: BidResultsState) => state.loading);

export const selectTreasuriesState: MemoizedSelector<
  unknown,
  USTreasuriesState
> = createSelector(selectSharedState, (state: State) => state.treasuries);

export const selectTreasuries = createSelector(
  selectTreasuriesState,
  getUSTreasuries
);

export const selectManagerState = createSelector(
  selectSharedState,
  (state: State) => state.manager
);

export const selectManagerFiltersState = createSelector(
  selectSharedState,
  (state: State) => state.managerFilters
);

export const selectManagerDeals: MemoizedSelector<
  unknown,
  DealVersion[]
> = createSelector(selectManagerState, getManagerDeals);

export const selectManagerLoadingState: MemoizedSelector<
  unknown,
  boolean
> = createSelector(selectManagerState, selectManagerLoading);

export const selectManagerLoadedState: MemoizedSelector<
  unknown,
  boolean
> = createSelector(selectManagerState, selectManagerLoaded);

export const selectManagerErrorState: MemoizedSelector<
  unknown,
  string
> = createSelector(selectManagerState, selectManagerError);
export const selectManagerStatusState: MemoizedSelector<
  unknown,
  boolean
> = createSelector(selectManagerState, selectManagerStatus);

export const selectManagerSortModelState: MemoizedSelector<
  unknown,
  { colId: ManagerGridColumnName; sort: SortOrder }[]
> = createSelector(selectManagerState, selectManagerSortModel);

export const selectManagerSelectionModelState: MemoizedSelector<
  unknown,
  string[]
> = createSelector(selectManagerState, selectManagerSelectionModel);

export const selectManagerFiltersFiltersState: MemoizedSelector<
  unknown,
  CalendarFiltersState
> = createSelector(selectManagerFiltersState, selectManagerFilters);

export const selectManagerFiltersCardsState: MemoizedSelector<
  unknown,
  CalendarFiltersState
> = createSelector(selectManagerFiltersState, selectManagerCardsFilters);

export const selectManagerFiltersDefaultsState: MemoizedSelector<
  unknown,
  CalendarFiltersState
> = createSelector(selectManagerFiltersState, selectManagerDefaults);

export const selectManagerFiltersUnsavedDefaultsState: MemoizedSelector<
  unknown,
  CalendarFiltersState
> = createSelector(selectManagerFiltersState, selectManagerDefaultsUnsaved);

export const selectDealCardsBidResults: MemoizedSelector<
  SharedState,
  DealCardsBidResultsState
> = createSelector(
  selectSharedState,
  (state: State) => state.dealCardsBidResults
);

export const getDesignationState = createSelector(
  selectSharedState,
  (state: State) => state.designations
);

export const getDesiEntryState = createSelector(
  getDesignationState,
  (state: DesignationState): DesignationEntryState => state.desiEntryData
);

export const selectAllotmentsData = createSelector(
  getDesignationState,
  getAllotmentsData
);

export const selectAllotments = createSelector(
  getDesignationState,
  getAllotments
);

export const selectAllotmentsLoadingState = createSelector(
  getDesignationState,
  getAllotmentsLoadingState
);

export const selectDesignationIssues = createSelector(
  getDesignationState,
  getDesignationIssues
);

export const selectDesignationIssuesLoadingState = createSelector(
  getDesignationState,
  getDesignationIssuesLoadingState
);

export const getDesignationsCardSortModelSelector = (
  key: DesignationsCardTypes
) => createSelector(getDesignationState, selectDesignationsCardSortModel(key));

export const selectDesignationDealInfo = createSelector(
  getDesignationState,
  getDesignationDealInfo
);

export const selectDesignationDealPriorityPolicy = createSelector(
  getDesignationState,
  getDesignationDealPriorityPolicy
);

export const selectDesignationDealInfoLoadingState = createSelector(
  getDesignationState,
  getDesignationDealInfoLoadingState
);

export const selectDesignationDealInfoLoadedState = createSelector(
  getDesignationState,
  getDesignationDealInfoLoadedState
);

export const selectSelectedAllotmentRows = createSelector(
  getDesignationState,
  getSelectedAllotments
);

export const selectDesignationsTabState = createSelector(
  selectSharedState,
  (state: State) => state.designationsTab
);

export const selectDesignationsTabLoadingState = createSelector(
  selectDesignationsTabState,
  (state) => state.loading // change this to deals loading once deals are added
);

export const selectBreakdowns = createSelector(
  getDesignationState,
  getBreakdowns
);

export const selectAllotmentsBreakdownsLoadingState = createSelector(
  getDesignationState,
  getAllotmentsBreakdownsLoadingState
);

export const selectDesignationEntryFinalStatus = createSelector(
  getDesignationState,
  getDesignationsFinalStatus
);
export const selectAllotmentsHasDifferentBreakdowns = createSelector(
  getDesignationState,
  getAllotmentsHasDifferentBreakdowns
);

export const selectBreakdownsSubmissionError = createSelector(
  getDesignationState,
  getBreakdownsSubmissionError
);

export const isBreakdownsSubmissionError = createSelector(
  getDesignationState,
  hasBreakdownsSubmissionError
);

export const selectIsBreakdownFormDirty = createSelector(
  getDesignationState,
  getBreakdownFormDirtyState
);

export const selectActiveTab = createSelector(
  getDesignationState,
  (state: DesignationState): string => state.activeTab
);

export const selectIsDesiApisLoaded = createSelector(
  getDesignationState,
  isDesiApisLoaded
);

export const selectIsDesiApisHasError = createSelector(
  getDesignationState,
  isDesiApisHasError
);

export const selectDesiBreakdownsSubmitting = createSelector(
  getDesignationState,
  isDesiBreakdownsSubmitting
);

export const isDesiEntryStatusLoading = createSelector(
  getDesignationState,
  (state: DesignationState) => state.desiEntryData.loading
);
export const selectDisableDesignationsExport = createSelector(
  selectDesignationIssues,
  selectDesignationIssuesLoadingState,
  (issues: DesignationIssue[], loading: boolean): boolean =>
    !issues || issues?.length === 0 || loading
);

export const selectIssuesByDesignationStatus = (
  issueStatus: DesignationsCardTypes
) =>
  createSelector(
    getDesignationState,
    getIssuesByDesignationStatus.bind(null, issueStatus)
  );
